import React from 'react';
import { Plus, X, Target, Lightbulb, MessageSquare, AlertTriangle, User, Zap, Users, Shield, Briefcase, BookOpen, CheckSquare, Sparkles, Award } from 'lucide-react';

const stepTypeOptions = [
  { value: 'goal', label: 'Цель сценария', icon: Target, color: 'blue' },
  { value: 'character', label: 'О персонаже', icon: User, color: 'orange' },
  { value: 'methods', label: 'Ключевые методы', icon: Lightbulb, color: 'purple' },
  { value: 'examples', label: 'Примеры фраз', icon: MessageSquare, color: 'green' },
  { value: 'mistakes', label: 'Типичные ошибки', icon: AlertTriangle, color: 'red' },
  { value: 'triggers', label: 'Психологические триггеры', icon: Zap, color: 'yellow' },
  { value: 'lpr_types', label: 'Типы ЛПР', icon: Users, color: 'indigo' },
  { value: 'objections', label: 'Возражения', icon: Shield, color: 'pink' },
  { value: 'services', label: 'Услуги', icon: Briefcase, color: 'cyan' },
  { value: 'laws', label: 'Законы общения', icon: BookOpen, color: 'slate' },
  { value: 'checklist', label: 'Чек-лист', icon: CheckSquare, color: 'teal' },
  { value: 'tricks', label: 'Хитрости и секреты', icon: Sparkles, color: 'violet' },
  { value: 'context', label: 'Контекст ситуации', icon: BookOpen, color: 'gray' },
  { value: 'success', label: 'Признаки успеха', icon: Award, color: 'emerald' },
];

const itemStepTypes = new Set([
  'character',
  'methods',
  'triggers',
  'lpr_types',
  'objections',
  'services',
  'laws',
  'checklist',
  'tricks',
  'context',
  'success',
]);

const itemFieldLabels = {
  character: 'Пункты о персонаже',
  methods: 'Методы',
  triggers: 'Триггеры',
  lpr_types: 'Типы ЛПР',
  objections: 'Возражения',
  services: 'Услуги',
  laws: 'Правила',
  checklist: 'Пункты чек-листа',
  tricks: 'Хитрости',
  context: 'Пункты контекста',
  success: 'Признаки успеха',
};

const OnboardingEditor = ({ onboardingSteps, onChange }) => {
  const addStep = () => {
    const newStep = {
      type: 'goal',
      title: '',
      content: '',
      items: [],
      examples: [],
      mistakes: [],
    };
    onChange([...onboardingSteps, newStep]);
  };

  const removeStep = (index) => {
    const newSteps = onboardingSteps.filter((_, i) => i !== index);
    onChange(newSteps);
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...onboardingSteps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    onChange(newSteps);
  };

  const addItemToStep = (stepIndex, field) => {
    const newSteps = [...onboardingSteps];
    newSteps[stepIndex][field] = [...(newSteps[stepIndex][field] || []), ''];
    onChange(newSteps);
  };

  const removeItemFromStep = (stepIndex, field, itemIndex) => {
    const newSteps = [...onboardingSteps];
    newSteps[stepIndex][field] = (newSteps[stepIndex][field] || []).filter((_, i) => i !== itemIndex);
    onChange(newSteps);
  };

  const updateStepItem = (stepIndex, field, itemIndex, value) => {
    const newSteps = [...onboardingSteps];
    newSteps[stepIndex][field] = [...(newSteps[stepIndex][field] || [])];
    newSteps[stepIndex][field][itemIndex] = value;
    onChange(newSteps);
  };

  const addExample = (stepIndex) => {
    const newSteps = [...onboardingSteps];
    newSteps[stepIndex].examples = [
      ...(newSteps[stepIndex].examples || []),
      { situation: '', phrase: '' }
    ];
    onChange(newSteps);
  };

  const removeExample = (stepIndex, exampleIndex) => {
    const newSteps = [...onboardingSteps];
    newSteps[stepIndex].examples = newSteps[stepIndex].examples.filter((_, i) => i !== exampleIndex);
    onChange(newSteps);
  };

  const updateExample = (stepIndex, exampleIndex, field, value) => {
    const newSteps = [...onboardingSteps];
    newSteps[stepIndex].examples[exampleIndex][field] = value;
    onChange(newSteps);
  };

  const getStepIcon = (type) => {
    const option = stepTypeOptions.find(opt => opt.value === type);
    return option ? option.icon : Target;
  };

  return (
    <div className="border-t pt-4 space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Онбординг перед тренировкой
        </h3>
        <button
          type="button"
          onClick={addStep}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Добавить шаг
        </button>
      </div>

      {onboardingSteps.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          Онбординг не настроен. Нажмите "Добавить шаг" для начала.
        </p>
      )}

      {onboardingSteps.map((step, stepIndex) => {
        const Icon = getStepIcon(step.type);
        const typeOption = stepTypeOptions.find(opt => opt.value === step.type);

        return (
          <div key={stepIndex} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
            {/* Step Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`p-1.5 bg-${typeOption?.color}-100 text-${typeOption?.color}-600 rounded`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className="font-medium text-gray-700">Шаг {stepIndex + 1}</span>
              </div>
              <button
                type="button"
                onClick={() => removeStep(stepIndex)}
                className="text-red-600 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Step Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Тип шага
              </label>
              <select
                value={step.type}
                onChange={(e) => updateStep(stepIndex, 'type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {stepTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Step Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Заголовок шага
              </label>
              <input
                type="text"
                value={step.title}
                onChange={(e) => updateStep(stepIndex, 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Например: Цель сценария"
              />
            </div>

            {/* Step Content (main text) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Основной текст
              </label>
              <textarea
                value={step.content || ''}
                onChange={(e) => updateStep(stepIndex, 'content', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Основное описание для этого шага..."
              />
            </div>

            {/* Items */}
            {itemStepTypes.has(step.type) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {itemFieldLabels[step.type] || 'Пункты'}
                  </label>
                  <button
                    type="button"
                    onClick={() => addItemToStep(stepIndex, 'items')}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Добавить
                  </button>
                </div>
                <div className="space-y-2">
                  {(step.items || []).map((item, itemIndex) => (
                    <div key={itemIndex} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateStepItem(stepIndex, 'items', itemIndex, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`${itemFieldLabels[step.type] || 'Пункт'} ${itemIndex + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeItemFromStep(stepIndex, 'items', itemIndex)}
                        className="px-2 text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Examples (for examples type) */}
            {step.type === 'examples' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Примеры фраз
                  </label>
                  <button
                    type="button"
                    onClick={() => addExample(stepIndex)}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Добавить пример
                  </button>
                </div>
                <div className="space-y-3">
                  {(step.examples || []).map((example, exampleIndex) => (
                    <div key={exampleIndex} className="border border-gray-300 rounded-lg p-3 space-y-2 bg-white">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-500">Пример {exampleIndex + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeExample(stepIndex, exampleIndex)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={example.situation || ''}
                        onChange={(e) => updateExample(stepIndex, exampleIndex, 'situation', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ситуация (необязательно)"
                      />
                      <textarea
                        value={example.phrase || ''}
                        onChange={(e) => updateExample(stepIndex, exampleIndex, 'phrase', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                        placeholder="Фраза для использования"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mistakes (for mistakes type) */}
            {step.type === 'mistakes' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Типичные ошибки
                  </label>
                  <button
                    type="button"
                    onClick={() => addItemToStep(stepIndex, 'mistakes')}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Добавить ошибку
                  </button>
                </div>
                <div className="space-y-2">
                  {(step.mistakes || []).map((mistake, mistakeIndex) => (
                    <div key={mistakeIndex} className="flex gap-2">
                      <input
                        type="text"
                        value={mistake}
                        onChange={(e) => updateStepItem(stepIndex, 'mistakes', mistakeIndex, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Ошибка ${mistakeIndex + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeItemFromStep(stepIndex, 'mistakes', mistakeIndex)}
                        className="px-2 text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OnboardingEditor;
